import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  Coupon1Icon,
  DeleteIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Show,
  StatusField,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { CouponList } from "interfaces/Revenue";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteCouponMutation,
  useGetCouponsQuery,
} from "store/features/Revenue/CouponApi";
import { numberToCurrency } from "utils";
import { handleCapitalize } from "utils/common";
import { extraColumnsField, htmlToText } from "utils/helpers";

const Coupon = () => {
  const navigate = useNavigate();

  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [selectCouponId, setSelectCouponId] = useState<any>(null);

  const requestBody = useGenerateQueryParams("coupon");
  const { data, isFetching } = useGetCouponsQuery(requestBody);

  const [deleteCoupon, { isLoading }] = useDeleteCouponMutation();

  const handleDeleteCoupon = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<CouponList>,
  ) => {
    event.stopPropagation();
    setSelectCouponId(params.id);
    setConfirmationModal(event.currentTarget);
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Coupon Name (${data?.totalCount || 0})`,
        "name",
        200,
        1,
      ),
    },
    {
      ...extraColumnsField("Discount", "discountValue", 100, 1),
      type: "number",
      valueGetter: (params) =>
        params?.row?.couponType === "FIXED"
          ? numberToCurrency(params?.row?.discountValue)
          : `${params?.row?.discountValue}%`,
    },
    {
      ...extraColumnsField("Status", "isActive", 110, 1),
      renderCell: (row) => (
        <StatusField statusString={row?.row?.isActive?.toString()} />
      ),
    },
    {
      ...extraColumnsField("User Type", "applyOn", 170, 1),
      valueFormatter: (params) => handleCapitalize(params?.value),
    },
    {
      ...extraColumnsField("Remarks", "description", 200, 1),
      valueGetter: (params) => htmlToText(params.row.description),
    },
    {
      ...extraColumnsField("Actions", "action", 120, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Grid2 container gap={1} display={"flex"} justifyContent={"flex-end"}>
          <IconButton
            className="outline"
            color="error"
            disabled={params.row.id === selectCouponId && isLoading}
            onClick={(event) => handleDeleteCoupon(event, params)}
          >
            <DeleteIcon width={20} />
          </IconButton>
        </Grid2>
      ),
    },
  ];

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        maxHeight={"calc(100vh - 312px)"}
        name="coupon"
        tableName={"coupon"}
        totalCount={data?.totalCount || 0}
        headerComponent={
          <GKButton
            variant="contained"
            onClick={() => navigate("/app/revenue/coupon")}
            startIcon={<PlusIcon width={16} />}
          >
            Create New Coupon
          </GKButton>
        }
        onRowClick={(row) => navigate(`/app/revenue/coupon/${row.id}/`)}
        emptyComponent={
          <NoData
            text="Coupons Await!"
            subText="Boost engagement with special offers and personalized discounts."
            customImg={
              <Coupon1Icon height={80} width={80} color="var(--neutral-100)" />
            }
            additionalComponent={
              <GKButton
                variant="contained"
                onClick={() => navigate("/app/revenue/coupon")}
                startIcon={<PlusIcon width={16} />}
              >
                Create New Coupon
              </GKButton>
            }
          />
        }
      />

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={async () => {
            try {
              await deleteCoupon(selectCouponId).unwrap();
              setSelectCouponId(null);
            } catch (err) {
              console.error("Delete coupon failed:", err);
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default Coupon;
